import React, { useEffect, useMemo, useRef, useState, useCallback, Suspense } from 'react';
import { createAuthFetch } from '../../utils/authFetch';
import { useNavigate } from 'react-router-dom';
import {Spinner} from "react-bootstrap";

const EChart = React.lazy(() => import("./EChart"));

const cache = new Map();

export default function AnalysisChart({
  cacheKey,
  endpoint,
  params,
  buildOption,
  onViewDetails,
  onDataLoaded,
}) {

  const chartRef = useRef(null);
  const navigate = useNavigate();
  const authFetch = useMemo(() => createAuthFetch(navigate), [navigate]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleChartClick = useCallback((params) => {
    if (params.componentType !== 'series' && params.componentType !== 'markPoint')
      return;

    // 👉 Клік по трикутнику (markPoint)
    if (params.componentType === 'markPoint') {
      const xIndex = params.data?.coord?.[0];
      const label = params.data?.labelKey;

      if (xIndex == null || !label) return;

      onViewDetails?.({
        monthIndex: xIndex,
        monthLabel: label,
      });
      return;
    }

    // 👉 Клік по бару / лінії
    onViewDetails?.({
      monthIndex: params.dataIndex,
      monthLabel: params.name,
    });
  }, [onViewDetails]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (cache.has(cacheKey)) {
        setData(cache.get(cacheKey));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await authFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(params),
        });

        const json = await res.json();
        //console.log(json);
        if (json.statusCode!=200) throw new Error('Failed to load analytics');

        cache.set(cacheKey, json.data);

        if (!cancelled) setData(json.data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => (cancelled = true);
  }, [cacheKey, endpoint, params, authFetch]);


  useEffect(() => {
    if (data) {
      onDataLoaded?.(data);
    }
  }, [data, onDataLoaded]);

  if (loading) return <div className="p-5 my-4"><Spinner animation="grow" variant="secondary" size="sm"/><span className="visually-hidden">Loading...</span></div>;
  if (error) return <div className="chart-error">Failed to load chart</div>;
  if (!data) return null;

  const option = buildOption(data);

  return (
    <Suspense>
      <EChart
        option={option}
        onChartClick={handleChartClick}
      />
    </Suspense>
  );
}
